import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ElementUpdateTypes, GroupElement } from '@bynder-studio/render-web';
import { useTranslate } from '@bynder/localization';
import { IconError, IconHideSmall, IconLockOpenSmall, IconLockSmall, IconShowSmall } from '@bynder/icons';
import { IconComponent } from 'packages/common/icons/IconComponent';
import { token, Tooltip } from '@bynder/design-system';
import EditableLayerItem from '~/common/editor/layers/layerItem/editableLayerItem/EditableLayerItem';
import { collectTopLevelElements, isElementLocked } from '~/common/editor/timeline/timeline-actions/components/utils';
import { isCtrlKey } from '~/helpers/hotKeys';
import useElementRenameShortcuts from 'packages/hooks/shortcuts/useElementRenameShortcuts';
import useEditor from '~/hooks/useEditor';
import useForceUpdate from '~/hooks/useForceUpdate';
import { getElementType, isUsedAsMask } from '~/common/editor/helpers/elementtree';
import generateTestId from '~/helpers/testIdHelpers';
import GenerateIcon from './GenerateIcon';
import { parseShortcutForTooltip } from 'packages/common/WithShortcutLabel';
import { LayerItemContainer, WarningIconWrapper } from './LayerItem.styled';

const LayerItem = ({
    element,
    childrenLength,
    onGroupExpand,
    level = 0,
    isDragging = false,
    isGroupExpand = false,
    isDragOverlay = false,
    isDragOver = false,
}) => {
    const { name: layerName, id, contentPropertyId } = element;
    const forceUpdate = useForceUpdate();
    const { translate } = useTranslate();
    const { creativeModel, manipulationRenderer, validationManager } = useEditor();
    const [selected, setSelected] = useState(() => manipulationRenderer?.isElementSelected(id) ?? false);
    const [enableEditor, setEnableEditor] = useState(false);
    const { isValid, isChildrenValid, validationMessages } =
        validationManager?.getElementValidationState(element.id) || {};
    useElementRenameShortcuts({ id, callback: () => setEnableEditor(true) });
    const validationMessage = useMemo(
        () => (validationMessages ? Object.values(validationMessages).join(',\n') : ''),
        [validationMessages],
    );

    const isGroup = element instanceof GroupElement;
    const isHidden = element.hidden;
    const isInDESSubtree = (el) => {
        const parent = el.parent;

        return parent instanceof GroupElement && parent.onlyAllowOneSelection;
    };
    const isInactiveDESChild =
        element.parent instanceof GroupElement &&
        element.parent.onlyAllowOneSelection &&
        element.parent.activeDropdownElementId !== element.id;

    const elementLockStatus = isElementLocked(element);
    // const elementLockStatus = element.locked;

    const visibilityIcon = isHidden ? <IconHideSmall /> : <IconShowSmall />;
    const lockedIcon = elementLockStatus ? <IconLockSmall /> : <IconLockOpenSmall />;
    const groupClassName = isGroup && 'layers--item-group';
    const isSelected = selected && 'layers--item-selected';
    const isContentProperty = contentPropertyId && 'layers--item-content-property';
    const visibilityClassName = isHidden && 'layers--item-hidden';
    const inactiveClassName = isInactiveDESChild && 'layers--item-inactive';
    const lockClassName = elementLockStatus && 'layers--item-locked';
    const dragOverlayClassName = isDragOverlay && 'layers--item-drag-overlay';
    const draggingClassName = isDragging && 'layers--item-dragging';
    const dragOverClassName = isDragOver && 'layers--item-drag-over';

    const getElementTypeForTestId = (el) => {
        const isMask = isUsedAsMask(creativeModel, el);
        const isElementMasked = el?.mask !== null;
        const type = getElementType(el);
        const postfix = isMask ? '_mask' : isElementMasked ? '_masked' : '';

        return `${type}${postfix}`;
    };

    const type = getElementTypeForTestId(element).toLowerCase();
    const testId = useMemo(() => generateTestId(`layer_element_${type}_${id}`), [type, id]);

    if ('data-testid' in testId && isGroup) {
        testId['data-children-length'] = childrenLength;
    }
    const handleClick = useCallback(
        (event) => {
            const isMultiSelectMode = isCtrlKey(event);
            manipulationRenderer.selectElement(id, isMultiSelectMode);
        },
        [id, manipulationRenderer],
    );

    const handleGroupExpansion = useCallback(() => {
        onGroupExpand(element.id);
    }, [element, onGroupExpand]);

    const toggleLock = useCallback(
        (event) => {
            event.preventDefault();

            manipulationRenderer.selectElement(id);
            // todo: Remove after BE implements inherited locks
            manipulationRenderer.getSelectedElements().forEach((el) => {
                creativeModel.updateElement(el.id, { locked: !elementLockStatus });
            });
            // creativeModel.updateElement(id, { locked: !elementLockStatus });
            forceUpdate();
        },
        [id, elementLockStatus, creativeModel, manipulationRenderer],
    );

    const toggleVisibility = useCallback(
        (event) => {
            event.preventDefault();

            manipulationRenderer.selectElement(id);
            collectTopLevelElements(manipulationRenderer.getSelectedElements()).forEach((el) => {
                creativeModel.updateElement(el.id, { hidden: !isHidden });
            });
            forceUpdate();
        },
        [manipulationRenderer, creativeModel, id, isHidden],
    );

    const handleDblClick = () => {
        setEnableEditor(true);
    };

    const handleElementNameChange = useCallback(
        (name) => {
            if (name === layerName) {
                return;
            }

            creativeModel.updateElement(id, { name });
            setEnableEditor(false);
        },
        [creativeModel, id],
    );

    useEffect(() => {
        if (!manipulationRenderer) {
            return;
        }

        setSelected(manipulationRenderer.isElementSelected(id));

        const elementSelectedListener = () => setSelected(manipulationRenderer.isElementSelected(id));

        manipulationRenderer.eventEmitter.on('elementSelected', elementSelectedListener);

        return () => manipulationRenderer.eventEmitter.off('elementSelected', elementSelectedListener);
    }, [id, manipulationRenderer]);

    useEffect(() => {
        if (!creativeModel) {
            return;
        }

        const unsubscribeName = creativeModel.onElementPartialUpdate(id, ElementUpdateTypes.NAME, forceUpdate);
        const unsubscribeLock = creativeModel.onElementPartialUpdate(id, ElementUpdateTypes.LOCK, forceUpdate);
        const unsubscribeHidden = creativeModel.onElementPartialUpdate(id, ElementUpdateTypes.VISIBILITY, forceUpdate);
        const unsubscribeMask = creativeModel.onElementPartialUpdate(id, ElementUpdateTypes.MASK, forceUpdate);

        // Listen to parent's activeDropdownElementId changes to update inactive DES child visual
        const unsubscribeParentDES = element.parent?.id
            ? creativeModel.onElementPartialUpdate(
                  element.parent.id,
                  ElementUpdateTypes.ACTIVE_DROPDOWN_ELEMENT_ID,
                  forceUpdate,
              )
            : null;

        return () => {
            unsubscribeName();
            unsubscribeLock();
            unsubscribeHidden();
            unsubscribeMask();
            unsubscribeParentDES?.();
        };
    }, [id, creativeModel, forceUpdate]);

    if (enableEditor) {
        return (
            <div className="layers--item-wrapper">
                <EditableLayerItem
                    element={element}
                    name={layerName}
                    groupClassName={groupClassName}
                    isGroup={isGroup}
                    setEnableEditor={setEnableEditor}
                    handleElementNameChange={handleElementNameChange}
                />
            </div>
        );
    }

    return (
        <div className="layers--item-wrapper">
            <LayerItemContainer
                onClick={handleClick}
                onDoubleClick={handleDblClick}
                className={`${groupClassName || ''} ${isSelected || ''} ${visibilityClassName || ''} ${
                    inactiveClassName || ''
                } ${lockClassName || ''} ${dragOverlayClassName || ''} ${draggingClassName || ''} ${
                    dragOverClassName || ''
                } d-flex align-items-center ${isContentProperty || ''}`}
                level={level}
                {...testId}
            >
                {isGroup && (
                    <button
                        type="button"
                        className={`layers--item-group-expand btn p-0 border-0 ${isGroupExpand && 'active'}`}
                        onClick={handleGroupExpansion}
                    >
                        <span className="triangle--down" />
                    </button>
                )}
                <GenerateIcon element={element} src={element.src} />
                {(!isValid || !isChildrenValid) && (
                    <Tooltip content={validationMessage} position="top-right">
                        <WarningIconWrapper>
                            <IconError style={{ color: token.red400 }} />
                        </WarningIconWrapper>
                    </Tooltip>
                )}
                <div className="layers--item-content">
                    <span>{layerName}</span>
                </div>
                <div className="layers--item-action-btns d-flex align-items-center">
                    <button
                        type="button"
                        disabled={false}
                        className={`btn layers--item-btn p-0 m-0 ${
                            elementLockStatus ? 'layers--item-btn-visible' : ''
                        }`}
                        onClick={toggleLock}
                        title={`${translate('editor.layers.sidebar.lock-unlock')} (${parseShortcutForTooltip([
                            'cmd',
                            'l',
                        ])})`}
                    >
                        {lockedIcon}
                    </button>
                    <button
                        type="button"
                        className={`btn layers--item-btn p-0 m-0 ${isHidden ? 'layers--item-btn-visible' : ''}`}
                        onClick={toggleVisibility}
                        title={`${translate('editor.layers.sidebar.show-hide')} (${parseShortcutForTooltip([
                            'cmd',
                            'h',
                        ])})`}
                    >
                        {visibilityIcon}
                    </button>
                    {isInDESSubtree(element) && <IconComponent />}
                </div>
            </LayerItemContainer>
        </div>
    );
};

export default LayerItem;
